<script setup lang="ts">
/**
 * Imports
 */
import { computed, ref, onMounted } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import { instructorModule } from '@/store/instructor/module'
import { toastModule } from '@/store/toast/module'

const {
    SidePanel,
    Button: PocketButton,
    Input: PocketInput,
    Select: PocketSelect,
} = UIKit

const emit = defineEmits<{
    close: []
}>()

const props = defineProps<{
    instructorId: string
}>()

/**
 * Refs
 */

const accountType = ref({
    label: 'Admin',
})

const isLoading = ref(false)

/**
 * Computed properties
 */

const currentOrgInstructors = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructors()
})

const currentOrgInstructorUsers = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructorUsers()
})

const archivedInstructor = computed(() => {
    const instructors = currentOrgInstructors.value
    return instructors?.[props.instructorId]
})

const archivedInstructorUser = computed(() => {
    const instructorUsers = currentOrgInstructorUsers.value
    if (!archivedInstructor.value) {
        return null
    }
    const instructor = instructorUsers?.[archivedInstructor.value.user.objectId]
    return instructor
})

/**
 * Functions
 */

const emitClose = () => {
    emit('close')
}

const submitUnarchive = async () => {
    isLoading.value = true
    const instructorToUnarchive = archivedInstructor.value
    const instructorUserToUnarchive = archivedInstructorUser.value

    if (!(instructorToUnarchive && instructorUserToUnarchive)) {
        return
    }

    // Proceed with unarchive
    await instructorModule.actions.activateInstructor({
        instructorId: instructorToUnarchive.objectId,
    })

    toastModule.actions.displayToast({
        title: `${instructorToUnarchive.isAdmin ? 'Admin' : 'Instructor'}
        ${instructorUserToUnarchive.firstName} ${instructorUserToUnarchive.lastName} unarchived.`,
    })

    isLoading.value = false
    emitClose()
}

/**
 * Mounted
 */

onMounted(async () => {
    isLoading.value = true
    await Promise.all([
        instructorModule.actions.fetchCurrentOrgInstructors(),
        instructorModule.actions.fetchCurrentOrgInstructorUsers(),
    ])

    accountType.value = {
        label: archivedInstructor.value?.isAdmin ? 'Admin' : 'Instructor',
    }

    isLoading.value = false
})
</script>

<template>
    <Portal to="sidepanel">
        <SidePanel
            :name="`Archived ${archivedInstructor?.isAdmin ? 'Admin' : 'Instructor'}`"
            :isTeachSidePanel="true"
            class="archived-instructor"
            cancel-button-text="Cancel"
            @close="emitClose"
        >
            <PocketInput
                :modelValue="archivedInstructorUser?.email"
                :disabled="true"
                label="Colleague's Email"
                class="archived-instructor__field"
            />
            <PocketSelect
                :modelValue="accountType"
                :disabled="true"
                :data="[{label: 'Admin'}]"
                label="Account Type"
                class="archived-instructor__select-field"
            />
            <template #action>
                <PocketButton
                    :disabled="isLoading"
                    @click="submitUnarchive"
                >
                    Unarchive {{ accountType.label }}
                </PocketButton>
            </template>
        </SidePanel>
    </Portal>
</template>

<style lang="scss" scoped>
.archived-instructor {
    &__field {
        margin-bottom: 27px;
    }

    &__select-field {
        width: 240px;
    }
}
</style>
